import { SignupAttribution, UserRole } from '@inversor-hub/types';

type AnalyticsTracker = (eventName: string, eventProperties?: Record<string, unknown>) => void;

type Platform = 'web';
type AddInvestmentEntryPoint = 'dashboard' | 'portfolio' | 'empty_state' | 'ai' | 'direct';
type AIAssistantEntryPoint = 'dashboard' | 'portfolio' | 'investment' | 'opportunity';
type InterestEntryPoint = 'opportunities_list' | 'opportunity_detail';

interface StoredAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  referrerHost?: string;
  landingPath?: string;
  capturedAt: string;
}

interface ProductEventPropertiesMap {
  signup_completed: {
    role: UserRole;
    signup_method: string;
    acquisition_channel: string;
    platform: Platform;
  };
  onboarding_completed: {
    role: UserRole;
    onboarding_variant?: string;
    platform: Platform;
  };
  dashboard_viewed: {
    role: 'investor';
    platform: Platform;
  };
  opportunities_list_viewed: {
    role: 'investor';
    platform: Platform;
  };
  portfolio_viewed: {
    role: 'investor';
    has_investments: boolean;
    investment_count: number;
    platform: Platform;
  };
  add_investment_clicked: {
    entry_point: AddInvestmentEntryPoint;
    role: 'investor';
    platform: Platform;
  };
  investment_created: {
    investment_id: string;
    investment_type: string;
    entry_point: AddInvestmentEntryPoint;
    role: UserRole;
    platform: Platform;
  };
  ai_assistant_opened: {
    entry_point: AIAssistantEntryPoint;
    role: UserRole;
    platform: Platform;
  };
  ai_message_sent: {
    entry_point: AIAssistantEntryPoint;
    investment_id?: string;
    use_case?: string;
    role: UserRole;
    platform: Platform;
  };
  invoice_uploaded: {
    investment_id?: string;
    file_count: number;
    platform: Platform;
  };
  invoice_forwarding_address_copied: {
    investment_id?: string;
    platform: Platform;
  };
  invoice_draft_updated: {
    draft_id: string;
    investment_id?: string;
    platform: Platform;
  };
  invoice_draft_confirmed: {
    draft_id: string;
    investment_id?: string;
    platform: Platform;
  };
  invoice_draft_rejected: {
    draft_id: string;
    investment_id?: string;
    platform: Platform;
  };
  opportunity_viewed: {
    role: 'investor';
    platform: Platform;
    opportunity_id: string;
  };
  interest_sent: {
    opportunity_id: string;
    entry_point: InterestEntryPoint;
    selected_services_count: number;
    role: 'investor';
    platform: Platform;
  };
}

const ATTRIBUTION_STORAGE_KEY = 'ih.analytics.attribution';
const SESSION_EVENT_KEY_PREFIX = 'ih.analytics.session.';

export const ANALYTICS_PLATFORM: Platform = 'web';

const isBrowser = () => typeof window !== 'undefined';

const getSessionEventKey = (dedupeKey: string) => `${SESSION_EVENT_KEY_PREFIX}${dedupeKey}`;

function getStoredAttribution(): StoredAttribution | null {
  if (!isBrowser()) {
    return null;
  }

  const rawAttribution = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
  if (!rawAttribution) {
    return null;
  }

  try {
    return JSON.parse(rawAttribution) as StoredAttribution;
  } catch {
    return null;
  }
}

function getReferrerHost(referrer: string) {
  if (!referrer) {
    return undefined;
  }

  try {
    return new URL(referrer).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

function getAttributionFromSearch(search: string) {
  const searchParams = new URLSearchParams(search);

  return {
    utmSource: searchParams.get('utm_source') ?? undefined,
    utmMedium: searchParams.get('utm_medium') ?? undefined,
    utmCampaign: searchParams.get('utm_campaign') ?? undefined,
    utmContent: searchParams.get('utm_content') ?? undefined,
    utmTerm: searchParams.get('utm_term') ?? undefined,
  };
}

function deriveAcquisitionChannel(attribution: Partial<StoredAttribution> | null) {
  if (!attribution) {
    return 'direct';
  }

  if (attribution.utmSource && attribution.utmMedium) {
    return `${attribution.utmSource}/${attribution.utmMedium}`;
  }

  if (attribution.utmSource) {
    return attribution.utmSource;
  }

  if (attribution.referrerHost) {
    return attribution.referrerHost;
  }

  return 'direct';
}

export function persistAttributionContext(search: string, referrer: string, pathname: string) {
  if (!isBrowser()) {
    return;
  }

  const existingAttribution = getStoredAttribution();
  if (existingAttribution) {
    return;
  }

  const utmAttribution = getAttributionFromSearch(search);
  const referrerHost = getReferrerHost(referrer);
  const hasAttribution = Object.values(utmAttribution).some(Boolean) || Boolean(referrerHost);

  if (!hasAttribution) {
    return;
  }

  const nextAttribution: StoredAttribution = {
    ...utmAttribution,
    referrerHost,
    landingPath: pathname,
    capturedAt: new Date().toISOString(),
  };

  window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(nextAttribution));
}

export function getAcquisitionChannel() {
  if (!isBrowser()) {
    return 'direct';
  }

  const storedAttribution = getStoredAttribution();
  if (storedAttribution) {
    return deriveAcquisitionChannel(storedAttribution);
  }

  return deriveAcquisitionChannel({
    ...getAttributionFromSearch(window.location.search),
    referrerHost: getReferrerHost(document.referrer),
  });
}

export function getSignupAttribution(): SignupAttribution | undefined {
  if (!isBrowser()) {
    return undefined;
  }

  const attribution = getStoredAttribution() ?? getAttributionFromSearch(window.location.search);
  const signupAttribution: SignupAttribution = {
    utmSource: attribution.utmSource,
    utmMedium: attribution.utmMedium,
    utmCampaign: attribution.utmCampaign,
    utmContent: attribution.utmContent,
    utmTerm: attribution.utmTerm,
  };

  return Object.values(signupAttribution).some(Boolean) ? signupAttribution : undefined;
}

export function trackProductEvent<K extends keyof ProductEventPropertiesMap>(
  trackEvent: AnalyticsTracker,
  eventName: K,
  eventProperties: ProductEventPropertiesMap[K]
) {
  trackEvent(eventName, eventProperties);
}

export function trackProductEventOncePerSession<K extends keyof ProductEventPropertiesMap>(
  trackEvent: AnalyticsTracker,
  dedupeKey: string,
  eventName: K,
  eventProperties: ProductEventPropertiesMap[K]
) {
  if (!isBrowser()) {
    trackProductEvent(trackEvent, eventName, eventProperties);
    return;
  }

  const sessionEventKey = getSessionEventKey(dedupeKey);
  if (window.sessionStorage.getItem(sessionEventKey) === '1') {
    return;
  }

  trackProductEvent(trackEvent, eventName, eventProperties);
  window.sessionStorage.setItem(sessionEventKey, '1');
}

export function inferAIUseCase(message: string) {
  const normalizedMessage = message.trim().toLowerCase();

  if (!normalizedMessage) {
    return 'empty';
  }

  if (
    normalizedMessage.includes('riesgo') ||
    normalizedMessage.includes('alerta') ||
    normalizedMessage.includes('risk')
  ) {
    return 'risk_detection';
  }

  if (
    normalizedMessage.includes('mejora') ||
    normalizedMessage.includes('accion') ||
    normalizedMessage.includes('acción') ||
    normalizedMessage.includes('prioriz')
  ) {
    return 'improvement_actions';
  }

  if (
    normalizedMessage.includes('cashflow') ||
    normalizedMessage.includes('flujo de caja') ||
    normalizedMessage.includes('rentabilidad') ||
    normalizedMessage.includes('financ')
  ) {
    return 'financial_analysis';
  }

  if (
    normalizedMessage.includes('resume') ||
    normalizedMessage.includes('resumen') ||
    normalizedMessage.includes('estado')
  ) {
    return 'summary';
  }

  return 'custom_query';
}
